import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Badge, Box, Button, Center, Drawer, Group, Loader, ScrollArea, Stack, Text } from '@mantine/core';
import { IconMapPin, IconTrophy, IconUser } from '@tabler/icons-react';
import { getMovementLabel } from 'components/Leaderboard/movementLabel';
import { isMovementResultFilled } from 'components/Workout/logging';
import {
  STICKY_NAV_OFFSET,
  useIsLargeDisplay,
  WHITEBOARD_SIZES,
} from 'components/Workout/whiteboardDisplay';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import type { WorkoutRetrieve } from 'types';
import {
  DistanceUnitEnum,
  DISTANCE_UNIT_LABEL_MAP,
  ScoreTypeEnum,
  SCORE_TYPE_LABEL_MAP,
  WeightUnitEnum,
  WEIGHT_UNIT_LABEL_MAP,
} from 'types/enums';
import type { WorkoutSectionMovementRetrieve } from 'types/generated/models/WorkoutSectionMovementRetrieve';

import AthletesFilterMenu, { type AthleteStatusFilter } from './AthletesFilterMenu';
import LeaderboardPanel from './LeaderboardPanel';
import TableHeader from './TableHeader';
import WhiteboardAthleteRow from './WhiteboardAthleteRow';
import useWhiteboardResults from '../hooks/useWhiteboardResults';

// How long a cross-highlighted row (table ⇄ leaderboard) stays tinted after a click.
const HIGHLIGHT_DURATION_MS = 2000;

const getUnitLabel = (m: WorkoutSectionMovementRetrieve): string | null => {
  if (m.scoreType === ScoreTypeEnum.WEIGHT_SETS_REPS && m.weightUnit)
    return WEIGHT_UNIT_LABEL_MAP[m.weightUnit as WeightUnitEnum] ?? null;
  if (m.scoreType === ScoreTypeEnum.DISTANCE && m.distanceUnit)
    return DISTANCE_UNIT_LABEL_MAP[m.distanceUnit as DistanceUnitEnum] ?? null;
  return null;
};

interface AthletesTableProps {
  classId: string;
  workout: WorkoutRetrieve;
}

const AthletesTable = ({ classId, workout }: AthletesTableProps): JSX.Element => {
  const {
    athletes,
    movements,
    results,
    levels,
    isClassLoading,
    location,
    coachNames,
    handleCellChange,
    handleCellCommit,
    handleLevelChange,
    handleLevelCommit,
  } = useWhiteboardResults(workout, classId);

  const isDesktop = useIsLargeDisplay();
  const { trackLeaderboardToggled } = useStaffTracking();

  const [statusFilter, setStatusFilter] = useState<AthleteStatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [highlightedAthleteId, setHighlightedAthleteId] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(highlightTimeoutRef.current), []);

  const triggerHighlight = (athleteId: string): void => {
    clearTimeout(highlightTimeoutRef.current);
    setHighlightedAthleteId(athleteId);
    document.querySelector(`[data-athlete-id="${athleteId}"]`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    highlightTimeoutRef.current = setTimeout(() => setHighlightedAthleteId(null), HIGHLIGHT_DURATION_MS);
  };

  const isAthleteCompleted = (athleteKiloClassId: string): boolean => {
    const map = results[athleteKiloClassId] ?? {};
    return movements.every(m => {
      const cell = map[m.workoutSectionMovementId];
      return cell !== undefined && isMovementResultFilled(cell);
    });
  };

  const completedCount = useMemo(
    () => athletes.filter(a => isAthleteCompleted(a.athleteKiloClassId)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [athletes, results, movements],
  );

  const visibleAthletes = useMemo(() => {
    const lower = searchQuery.trim().toLowerCase();
    return athletes.filter(a => {
      if (statusFilter !== 'all') {
        const completed = isAthleteCompleted(a.athleteKiloClassId);
        if (statusFilter === 'completed' && !completed) return false;
        if (statusFilter === 'pending' && completed) return false;
      }
      if (lower && !a.athlete.fullName.toLowerCase().includes(lower)) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athletes, statusFilter, searchQuery, results, movements]);

  if (isClassLoading) {
    return (
      <Center py='xl'>
        <Loader />
      </Center>
    );
  }

  return (
    <Stack spacing='md'>
      {(location || coachNames) && (
        <Group spacing='lg'>
          {location && (
            <Group spacing={6}>
              <IconMapPin size={14} color='#868e96' />
              <Text size='sm' color='dimmed'>
                {location}
              </Text>
            </Group>
          )}
          {coachNames && (
            <Group spacing={6}>
              <IconUser size={14} color='#868e96' />
              <Text size='sm' color='dimmed'>
                {coachNames}
              </Text>
            </Group>
          )}
        </Group>
      )}
      <TableHeader
        trackedCount={movements.length}
        athleteCount={athletes.length}
        completedCount={completedCount}
        filterMenu={
          <AthletesFilterMenu
            status={statusFilter}
            onStatusChange={setStatusFilter}
            search={searchQuery}
            onSearchChange={setSearchQuery}
            visibleAthletes={visibleAthletes}
          />
        }
        leaderboardToggle={
          movements.length > 0 && (
            <Button
              size='xs'
              variant={leaderboardOpen ? 'filled' : 'default'}
              leftIcon={<IconTrophy size={14} />}
              onClick={() =>
                setLeaderboardOpen(prev => {
                  const next = !prev;
                  trackLeaderboardToggled({ opened: next });
                  return next;
                })
              }
            >
              Leaderboard
            </Button>
          )
        }
      />

      {/* A plain flex Box, not Group — Group forces `flex-grow: 0` on every direct
          child, which would keep the table from shrinking to make room for the panel. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 16,
          width: '100%',
          minWidth: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {athletes.length === 0 ? (
            <Center py='xl'>
              <Text color='dimmed'>No athletes registered for this class.</Text>
            </Center>
          ) : movements.length === 0 ? (
            <Center py='xl'>
              <Text color='dimmed'>No movements in this workout to track.</Text>
            </Center>
          ) : visibleAthletes.length === 0 ? (
            <Center py='xl'>
              <Text color='dimmed'>No athletes match the current filter.</Text>
            </Center>
          ) : (
            <ScrollArea
              type='auto'
              sx={theme => ({
                border: `1px solid ${theme.colors.gray[2]}`,
                borderRadius: theme.radius.md,
              })}
            >
              <Box sx={{ minWidth: 600 }}>
                <Box
                  component='table'
                  sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}
                >
                  <thead>
                    <tr>
                      <Box
                        component='th'
                        px='md'
                        py={isDesktop ? 'md' : 'xs'}
                        sx={theme => ({
                          position: 'sticky',
                          left: 0,
                          background: theme.white,
                          zIndex: 2,
                          textAlign: 'left',
                          fontSize: isDesktop ? WHITEBOARD_SIZES.resultsColumnMeta : theme.fontSizes.xs,
                          fontWeight: 600,
                          color: theme.colors.gray[6],
                          letterSpacing: '0.05em',
                          borderBottom: `1px solid ${theme.colors.gray[2]}`,
                          minWidth: isDesktop
                            ? WHITEBOARD_SIZES.athleteColumn.large
                            : WHITEBOARD_SIZES.athleteColumn.compact,
                          verticalAlign: 'top',
                        })}
                      >
                        ATHLETE
                      </Box>
                      {movements.map(movement => {
                        const { title, meta, isActiveBenchmark } = getMovementLabel(movement);
                        return (
                          <Box
                            component='th'
                            key={movement.workoutSectionMovementId}
                            px='md'
                            py={isDesktop ? 'md' : 'xs'}
                            sx={theme => ({
                              textAlign: 'left',
                              borderBottom: `1px solid ${theme.colors.gray[2]}`,
                              borderLeft: `1px solid ${theme.colors.gray[2]}`,
                              minWidth:
                                movement.scoreType === ScoreTypeEnum.WEIGHT_SETS_REPS
                                  ? isDesktop
                                    ? WHITEBOARD_SIZES.weightMovementColumn.large
                                    : WHITEBOARD_SIZES.weightMovementColumn.compact
                                  : isDesktop
                                  ? WHITEBOARD_SIZES.movementColumn.large
                                  : WHITEBOARD_SIZES.movementColumn.compact,
                              verticalAlign: 'top',
                            })}
                          >
                            <Stack spacing={isDesktop ? 6 : 4}>
                              {/* Title: the movement, or the section for whole-section scoring */}
                              <Text
                                size={isDesktop ? WHITEBOARD_SIZES.resultsColumnTitle : 'sm'}
                                weight={700}
                                sx={{ lineHeight: 1.25 }}
                              >
                                {title}
                              </Text>

                              {/* Section context (or the whole-section descriptor) — one line, never wraps */}
                              <Text
                                size={isDesktop ? WHITEBOARD_SIZES.resultsColumnMeta : 'xs'}
                                weight={600}
                                color='dimmed'
                                truncate
                                sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
                              >
                                {meta}
                              </Text>

                              {/* Meta: score type + unit + the single Benchmark accent */}
                              <Group spacing={6} align='center' sx={{ rowGap: 4 }}>
                                <Text
                                  size={isDesktop ? WHITEBOARD_SIZES.resultsColumnMeta : 'xs'}
                                  weight={600}
                                  sx={theme => ({ color: theme.colors.gray[7] })}
                                >
                                  {movement.scoreType === ScoreTypeEnum.WEIGHT_SETS_REPS
                                    ? `${movement.scoreSets.length} Sets`
                                    : SCORE_TYPE_LABEL_MAP[movement.scoreType as ScoreTypeEnum]}
                                </Text>
                                {getUnitLabel(movement) && (
                                  <Badge size={isDesktop ? 'md' : 'xs'} variant='outline' color='gray'>
                                    {getUnitLabel(movement)}
                                  </Badge>
                                )}
                                {isActiveBenchmark && (
                                  <Badge size={isDesktop ? 'md' : 'xs'} variant='light' color='blue'>
                                    Benchmark
                                  </Badge>
                                )}
                              </Group>
                            </Stack>
                          </Box>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleAthletes.map(row => (
                      <WhiteboardAthleteRow
                        key={row.athleteKiloClassId}
                        row={row}
                        movements={movements}
                        rowResults={results[row.athleteKiloClassId]}
                        rowLevels={levels[row.athleteKiloClassId]}
                        onChange={handleCellChange}
                        onCommit={handleCellCommit}
                        onLevelChange={handleLevelChange}
                        onLevelCommit={handleLevelCommit}
                        enlarged={isDesktop}
                        isHighlighted={row.athlete.athleteId === highlightedAthleteId}
                        onNameClick={() => triggerHighlight(row.athlete.athleteId)}
                      />
                    ))}
                  </tbody>
                </Box>
              </Box>
            </ScrollArea>
          )}
        </Box>

        {isDesktop && leaderboardOpen && movements.length > 0 && (
          <Box
            sx={theme => ({
              width: WHITEBOARD_SIZES.leaderboardPanel.large,
              flexShrink: 0,
              border: `1px solid ${theme.colors.gray[2]}`,
              borderRadius: theme.radius.md,
              position: 'sticky',
              top: STICKY_NAV_OFFSET,
              maxHeight: `calc(100vh - ${STICKY_NAV_OFFSET}px - ${theme.spacing.xl}px)`,
              overflowY: 'auto',
            })}
          >
            <LeaderboardPanel
              workoutId={workout.workoutId}
              movements={movements}
              sessionId={classId}
              highlightedAthleteId={highlightedAthleteId}
              onEntryClick={triggerHighlight}
            />
          </Box>
        )}
      </Box>

      {!isDesktop && (
        <Drawer
          opened={leaderboardOpen}
          onClose={() => setLeaderboardOpen(false)}
          position='bottom'
          size='85%'
          title={
            <Text weight={700} size='lg'>
              Leaderboard
            </Text>
          }
        >
          <LeaderboardPanel
            workoutId={workout.workoutId}
            movements={movements}
            sessionId={classId}
            highlightedAthleteId={highlightedAthleteId}
            onEntryClick={triggerHighlight}
            spacious
          />
        </Drawer>
      )}
    </Stack>
  );
};

export default AthletesTable;
